import { CustomKeyAttributes } from '@loyalty/shared-common/profile-api-client';
import { Fields } from '@react-awesome-query-builder/core';
import { getLocalizedProperty } from '@loyalty/shared-common';
import { BaseConfigSettings } from '../fields';
import {
  emailExcludedOperators,
  excludedOperators,
  isTestUserExcludedOperators,
} from '../settings';
import { shouldBeApositiveNumber } from './should-be-apositive-number';

export function profileFields(
  { currencies, levels, locale, t }: BaseConfigSettings,
  customKeys?: CustomKeyAttributes[],
): Fields {
  const fields: Fields = {
    birthdate: {
      label: t('rule.criteria.profile.birthdate'),
      type: 'date',
      preferWidgets: ['date'],
    },
    city: {
      label: t('rule.criteria.profile.city'),
      type: 'text',
      excludeOperators: excludedOperators,
      valueSources: ['value'],
    },
    country: {
      label: t('rule.criteria.profile.country'),
      type: 'text',
      excludeOperators: excludedOperators,
      valueSources: ['value'],
    },
    language: {
      label: t('rule.criteria.profile.language'),
      type: 'text',
      excludeOperators: excludedOperators,
      valueSources: ['value'],
    },
    zipCode: {
      label: t('rule.criteria.profile.zipcode'),
      type: 'text',
      excludeOperators: excludedOperators,
      valueSources: ['value'],
    },
    email: {
      label: t('rule.criteria.profile.email'),
      type: 'text',
      excludeOperators: emailExcludedOperators,
      valueSources: ['value'],
    },
    isTestUser: {
      label: t('rule.criteria.profile.isTestUser'),
      type: 'boolean',
      valueSources: ['value'],
      excludeOperators: isTestUserExcludedOperators,
    },
  };

  if (currencies?.length) {
    fields.currencies = {
      label: t('rule.criteria.profile.currencies'),
      type: '!group',
      subfields: {
        currencyId: {
          label: t('rule.criteria.kind'),
          type: 'select',
          valueSources: ['value'],
          fieldSettings: {
            listValues: currencies.map(currency => ({
              value: currency.id,
              title: getLocalizedProperty(currency, 'name', 'en'),
            })),
          },
        },
        value: {
          label: t('rule.criteria.amount'),
          type: 'number',
          fieldSettings: {
            validateValue: shouldBeApositiveNumber,
          },
          valueSources: ['value'],
          preferWidgets: ['number'],
        },
      },
    };
  }

  if (levels?.length) {
    fields['level'] = {
      label: t('rule.criteria.profile.level'),
      type: 'select',
      valueSources: ['value'],
      fieldSettings: {
        listValues: levels
          .sort((a, b) => a.minCurrencyAmount - b.minCurrencyAmount)
          .map(level => ({
            title:
              level.i18nAttributes[locale || 'en']?.name ||
              Object.values(level.i18nAttributes).pop()?.name,
            value: level.id,
          })),
      },
    };
  }

  const converType = {
    string: 'text',
    date: 'date',
    number: 'number',
    boolean: 'boolean',
  };

  if (customKeys?.length) {
    fields['customKeys'] = {
      label: t('rule.criteria.custom-keys'),
      type: '!struct',
      subfields: customKeys.reduce((result: Record<string, any>, item, index) => {
        result[customKeys[index].technicalName] = {
          label: customKeys[index].name,
          type: converType[customKeys[index].type],
          excludeOperators: excludedOperators,
          valueSources: ['value'],
        };
        return result;
      }, {}),
    };
  }

  return fields;
}
